import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ExistingCompanyForm from "./CompanySponsored/ExistingCompanyForm";
import NewCompanyForm from "./CompanySponsored/NewCompanyForm";
import SelfSponsoredForm from "./CompanySponsored/SelfSponsoredForm";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { fetchLookupOptions } from "../constants/lookupOptions";

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export default function EnrollmentMain() {
  const [sponsorType, setSponsorType] = useState("self");
  const [companyType, setCompanyType] = useState("");
  const [lookups, setLookups] = useState({ races: [], nationalities: [] });
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const summaryRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const loadLookups = async () => {
      const data = await fetchLookupOptions();
      setLookups(data);
    };
    loadLookups();
  }, []);

  // 🟢 RECEIVING DATA FROM CourseDetails
  const location = useLocation();
  const {
    title,
    selectedLevel,
    price,
    duration,
    totalPrice,
    schedule
  } = location.state || {};

  // GST Calculation
  const gstAmount = price ? (price * 0.09).toFixed(2) : 0;
  const totalWithGst = price ? (price + parseFloat(gstAmount)).toFixed(2) : 0;

  const formatDateString = (dateStr) => {
    if (!dateStr) return "TBC";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-SG", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const formatTime = (am_start, pm_end) => {
    if (!am_start) return "9:00 AM - 5:00 PM";
    const convert = (t) => {
      if (!t) return "";
      const [hStr, mStr] = t.split(":");
      const h = parseInt(hStr);
      if (isNaN(h)) return t;
      const ampm = h >= 12 ? "PM" : "AM";
      const displayH = h % 12 === 0 ? 12 : h % 12;
      return `${displayH}:${mStr || "00"} ${ampm}`;
    };
    const startDisp = convert(am_start);
    const endDisp = convert(pm_end || "17:00");
    return `${startDisp} - ${endDisp}`;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate header
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate course summary
      gsap.fromTo(
        summaryRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: summaryRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate form section
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.4,
          ease: "power2.out",
          scrollTrigger: {
            trigger: formRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <Navbar />

      {/* 🌿 HEADER */}
      <div
        className="relative h-[340px] bg-fixed bg-center bg-cover"
        style={{
          backgroundImage:
            "url('./mission.avif')",
        }}
      >
        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold mb-3"
          >
            {title || "Course Enrollment"}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-lg text-gray-200 max-w-2xl"
          >
            Please enter the information below to complete your registration.
          </motion.p>
        </div>
      </div>

      {/* 🟢 COURSE SUMMARY BOX */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-4xl mx-auto -mt-20 bg-white shadow-lg rounded-2xl p-8 border border-gray-200 z-10 w-full"
      >
        <h2 className="text-xl text-center font-bold text-greensafe-700 mb-4">
          Course Summary
        </h2>

        {title ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-gray-700 text-sm">
                <p className="md:col-span-2"><b>Course Name:</b> {title}</p>
                <p><b>Course Dates:</b> {formatDateString(schedule?.start_date)} ({formatTime(schedule?.am_start_time, schedule?.pm_end_time)})</p>
                <p><b>Assessment Dates:</b> {formatDateString(schedule?.examination_date || schedule?.end_date || schedule?.start_date)}</p>
                <p className="md:col-span-2"><b>Venue:</b> {schedule?.location_name || "Training Center"}</p>
                <p><b>Language:</b> {schedule?.course_language || "English"}</p>
              </div>

            {/* REGISTRATION TYPE SELECTION MOVED HERE */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-xl font-bold text-center text-greensafe-700 mb-6">
                Registration Type
              </h3>

              {/* STEP 1: SPONSOR */}
              <div className="flex justify-center gap-6 mb-6">
                <label className="flex items-center cursor-pointer font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 px-6 py-3 rounded-lg border border-gray-200 transition-all">
                  <input
                    type="radio"
                    name="sponsorType"
                    value="company"
                    checked={sponsorType === "company"}
                    onChange={() => {
                      setSponsorType("company");
                      setCompanyType("");
                    }}
                    className="mr-3 w-4 h-4 accent-greensafe-600"
                  />
                  Company Sponsored
                </label>

                <label className="flex items-center cursor-pointer font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 px-6 py-3 rounded-lg border border-gray-200 transition-all">
                  <input
                    type="radio"
                    name="sponsorType"
                    value="self"
                    checked={sponsorType === "self"}
                    onChange={() => {
                      setSponsorType("self");
                      setCompanyType("");
                    }}
                    className="mr-3 w-4 h-4 accent-greensafe-600"
                  />
                  Self Sponsored
                </label>
              </div>

              {/* STEP 2: COMPANY TYPE */}
              {sponsorType === "company" && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="text-center bg-blue-50/30 p-5 rounded-xl border border-blue-100 max-w-md mx-auto"
                >
                  <h4 className="text-sm font-bold mb-4 text-gray-800">
                    Select Company Type
                  </h4>

                  <div className="flex justify-center gap-4">
                    <label className="flex items-center cursor-pointer font-medium text-gray-700 bg-white hover:bg-gray-50 px-5 py-2.5 rounded-lg border border-gray-200 transition-all shadow-sm">
                      <input
                        type="radio"
                        name="companyType"
                        value="existing"
                        checked={companyType === "existing"}
                        onChange={() => setCompanyType("existing")}
                        className="mr-2 accent-blue-600"
                      />
                      Existing Client
                    </label>

                    <label className="flex items-center cursor-pointer font-medium text-gray-700 bg-white hover:bg-gray-50 px-5 py-2.5 rounded-lg border border-gray-200 transition-all shadow-sm">
                      <input
                        type="radio"
                        name="companyType"
                        value="new"
                        checked={companyType === "new"}
                        onChange={() => setCompanyType("new")}
                        className="mr-2 accent-blue-600"
                      />
                      New Company
                    </label>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-red-600">
            ⚠ No course selected. Return to Course Page.
          </p>
        )}
      </motion.div>

      {/* 🧾 REGISTRATION FORM */}
      <motion.div
        initial={{ opacity: 0, y: 70 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1 }}
        className="max-w-5xl mx-auto mt-6 p-2 w-full"
      >

        {/* STEP 3 — FIXED FORM LOADING BLOCK */}
        <div className="mt-6">
          {sponsorType === "self" && (
            <SelfSponsoredForm courseData={location.state} lookups={lookups} />
          )}

          {sponsorType === "company" && companyType === "existing" && (
            <ExistingCompanyForm courseData={location.state} lookups={lookups} />
          )}

          {sponsorType === "company" && companyType === "new" && (
            <NewCompanyForm courseData={location.state} lookups={lookups} />
          )}
        </div>
      </motion.div>

      <Footer />
    </div>
  );
}
